import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';

import dayjs from 'shared/helpers/dayjs';
import baseFetcher from 'shared/helpers/baseFetcher';
import ApiLoading from 'shared/ApiLoading';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import { TopBar } from 'topBar/components/TopBar';
import { useScrolledPast } from 'topBar/hooks/useScrolledPast';
import { useUser } from 'shared/hooks/api/user';
import { APIKey, apiRoutes } from 'constants/apiKeys';
import { EventDetail } from 'events/_types';
import { NoteRevision } from 'shiftNotes/_types';
import getNoteAuthorName from 'shiftNotes/helpers/getNoteAuthorName';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import EventVersionRevisionCard from 'events/components/EventVersionRevisionCard';
import useEventVersionHistoryMenuItems from 'events/hooks/useEventVersionHistoryMenuItems';
import formatRevisionEditorLabel from 'events/helpers/versionHistoryFormat';

import * as Text from 'theme/components/Text.style';
import * as Styled from 'events/styles/EventVersionHistoryPage.style';

interface EventVersionHistoryPageProps {
  event: EventDetail;
  onBack: () => void;
}

interface FlatRevision {
  revision: NoteRevision;
  noteId: string;
  noteUserId: string | undefined;
  noteAuthorName: string;
  isNoteCreation: boolean;
}

export default function EventVersionHistoryPage({
  event,
  onBack,
}: EventVersionHistoryPageProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const capture = usePosthogCapture();
  const { data: currentUser } = useUser();
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const isScrolledPast = useScrolledPast(titleRef);

  const noteResults = useQueries({
    queries: event.shift_notes.map((note) => ({
      queryKey: [APIKey.SHIFT_NOTE_REVISIONS, note.id],
      queryFn: () =>
        baseFetcher(apiRoutes.shiftNoteRevisions(note.id)) as Promise<
          NoteRevision[]
        >,
      enabled: !!note.id,
    })),
  });

  const isLoading = noteResults.some((r) => r.isLoading);

  const flatRevisions = useMemo<FlatRevision[]>(() => {
    const entries: FlatRevision[] = [];
    event.shift_notes.forEach((note, idx) => {
      const fetched = (noteResults[idx]?.data ?? []) as NoteRevision[];
      const revisions =
        fetched.length > 0
          ? fetched
          : [
              {
                text: note.edited_text,
                source: 'manual_edit' as const,
                created: note.created,
              },
            ];
      const oldestFirst = [...revisions].sort(
        (a, b) => dayjs(a.created).valueOf() - dayjs(b.created).valueOf()
      );
      const kept: NoteRevision[] = [];
      oldestFirst.forEach((revision) => {
        const isAi =
          revision.source === 'ai_edit' || revision.source === 'processing';
        const previous = kept[kept.length - 1];
        if (isAi && previous && previous.text === revision.text) return;
        kept.push(revision);
      });
      kept.forEach((revision, i) => {
        entries.push({
          revision,
          noteId: note.id,
          noteUserId: note.user?.id,
          noteAuthorName: getNoteAuthorName(note, t),
          isNoteCreation: i === 0,
        });
      });
    });
    return entries.sort(
      (a, b) =>
        dayjs(b.revision.created).valueOf() -
        dayjs(a.revision.created).valueOf()
    );
  }, [event.shift_notes, noteResults, t]);

  const exportText = useMemo(() => {
    if (flatRevisions.length === 0) return '';
    const currentUserId = currentUser?.id;
    const header = t('eventVersionHistory.exportHeader', {
      title: event.title,
    });
    const parts = flatRevisions.map(
      ({ revision, noteUserId, noteAuthorName, isNoteCreation }) => {
        const revisionDate = dayjs(revision.created);
        const format =
          revisionDate.year() !== dayjs().year()
            ? 'MMM D, YYYY, h:mm A'
            : 'MMM D, h:mm A';
        const editorLabel = formatRevisionEditorLabel({
          t,
          revision,
          noteAuthorName,
          isNoteCreation,
          isCurrentUser: !!currentUserId && currentUserId === noteUserId,
        });
        return `${revisionDate.format(format)}\n${editorLabel}\n\n${
          revision.text
        }`;
      }
    );
    return `${header}\n\n${parts.join('\n\n---\n\n')}`;
  }, [flatRevisions, currentUser, event.title, t]);

  const menuItems = useEventVersionHistoryMenuItems({ event, exportText });

  useEffect(() => {
    capture.eventVersionHistoryViewed({
      event_id: event.id,
      note_count: event.shift_notes.length,
    });
  }, [capture, event.id, event.shift_notes.length]);

  let body: JSX.Element;
  if (isLoading) {
    body = <ApiLoading />;
  } else if (flatRevisions.length === 0) {
    body = (
      <Styled.EmptyState>{t('eventVersionHistory.empty')}</Styled.EmptyState>
    );
  } else {
    body = (
      <Styled.RevisionStack>
        {flatRevisions.map(
          ({
            revision,
            noteId,
            noteUserId,
            noteAuthorName,
            isNoteCreation,
          }) => (
            <EventVersionRevisionCard
              key={`${noteId}-${revision.created}-${revision.source}`}
              revision={revision}
              noteAuthorName={noteAuthorName}
              noteId={noteId}
              isNoteCreation={isNoteCreation}
              isCurrentUser={!!currentUser && currentUser.id === noteUserId}
            />
          )
        )}
      </Styled.RevisionStack>
    );
  }

  return (
    <ErrorBoundary>
      <TopBar
        onBackClick={onBack}
        showBackButton
        showOnDesktop
        backButtonAriaLabel={t('eventVersionHistory.backAriaLabel')}
        title={t('eventVersionHistory.title')}
        showTitle={isScrolledPast}
        menuItems={menuItems}
        menuButtonAriaLabel={t('eventVersionHistory.menuButtonAriaLabel')}
      />
      <Styled.Page>
        <Styled.Inner>
          <Text.H3 ref={titleRef}>{t('eventVersionHistory.title')}</Text.H3>
          {body}
        </Styled.Inner>
      </Styled.Page>
    </ErrorBoundary>
  );
}
